import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getTimesheetMotifs, createTimesheetMotif, updateTimesheetMotif, deleteTimesheetMotif } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const TimesheetMotifManagement = () => {
  const toast = useToast();
  const [motifs, setMotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = async () => {
    try {
      const res = await getTimesheetMotifs();
      setMotifs(res.data || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createTimesheetMotif({ name: newName.trim() });
      setNewName('');
      toast.success('Motif créé');
      load();
    } catch { toast.error('Erreur création'); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateTimesheetMotif(id, { name: editName.trim() });
      setEditingId(null);
      toast.success('Motif modifié');
      load();
    } catch { toast.error('Erreur modification'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce motif ?')) return;
    try {
      await deleteTimesheetMotif(id);
      toast.success('Motif supprimé');
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Motifs feuille de temps</h3>
          <p className="text-sm text-gray-500 mt-1">Liste des motifs disponibles dans la colonne "Motif" des feuilles de temps.</p>
        </div>
        <div className="p-5">
          {/* Formulaire ajout */}
          <div className="flex gap-2 mb-5 p-4 bg-gray-50 rounded-lg">
            <input
              type="text" value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="Nouveau motif..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button onClick={handleCreate} disabled={!newName.trim()} className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm disabled:opacity-50">
              <Plus size={14} /> Ajouter
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Chargement...</p>
          ) : motifs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Aucun motif configuré.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {motifs.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        {editingId === m.id ? (
                          <input type="text" value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUpdate(m.id)} className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary" autoFocus />
                        ) : (
                          <span className="text-sm text-gray-700">{m.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === m.id ? (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleUpdate(m.id)} className="p-1.5 text-green-600 hover:bg-green-100 rounded"><Check size={14} /></button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded"><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => { setEditingId(m.id); setEditName(m.name); }} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"><Edit size={14} /></button>
                            <button onClick={() => handleDelete(m.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded"><Trash2 size={14} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimesheetMotifManagement;
